import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Box,
	CircularProgress,
	Snackbar,
	Alert,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

interface WorkerPoolOption {
	_id: string;
	poolName: string;
	poolType: string;
}

interface DefaultWorkerPoolsRecord {
	gsmWorkerPoolId?: string;
	gsmWorkerPoolName?: string;
	whatsappWorkerPoolId?: string;
	whatsappWorkerPoolName?: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
}

const EMPTY_FORM = {
	gsmWorkerPoolId: '',
	whatsappWorkerPoolId: '',
};

const renderPoolValue = (selected: unknown, pools: WorkerPoolOption[]) => {
	const id = String(selected || '');
	if (!id) {
		return <em>None</em>;
	}
	return pools.find((pool) => pool._id === id)?.poolName || id;
};

export const SystemNotificationDefaultWorkerPoolsDialog: React.FC<Props> = ({ open, onClose, onSave }) => {
	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);
	const [gsmPools, setGsmPools] = useState<WorkerPoolOption[]>([]);
	const [whatsappPools, setWhatsappPools] = useState<WorkerPoolOption[]>([]);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

	useEffect(() => {
		if (!open) return;

		const loadData = async () => {
			setLoading(true);
			setErrors({});
			try {
				const [poolsResponse, defaultsResponse] = await Promise.all([
					apiClient.get('/iu/workerpool'),
					apiClient.get('/system-notifications/default-worker-pools'),
				]);

				const pools: WorkerPoolOption[] = Array.isArray(poolsResponse.data) ? poolsResponse.data : [];
				setGsmPools(pools.filter((p) => p.poolType === 'GSM'));
				setWhatsappPools(pools.filter((p) => p.poolType === 'WhatsApp'));

				const defaults: DefaultWorkerPoolsRecord = defaultsResponse.data || {};
				setForm({
					gsmWorkerPoolId: defaults.gsmWorkerPoolId || '',
					whatsappWorkerPoolId: defaults.whatsappWorkerPoolId || '',
				});
			} catch (error) {
				Logger.error('SystemNotificationDefaultWorkerPoolsDialog', `Failed to load data: ${error}`);
				setGsmPools([]);
				setWhatsappPools([]);
				setForm(EMPTY_FORM);
				setErrors({ form: 'Failed to load worker pools or defaults' });
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [open]);

	const handleSave = async () => {
		setErrors({});
		setSaving(true);
		try {
			await apiClient.put('/system-notifications/default-worker-pools', {
				gsmWorkerPoolId: form.gsmWorkerPoolId,
				whatsappWorkerPoolId: form.whatsappWorkerPoolId,
			});
			setSnackbar({ open: true, message: 'Default worker pools saved', severity: 'success' });
			onSave();
		} catch (error) {
			Logger.error('SystemNotificationDefaultWorkerPoolsDialog', `Failed to save defaults: ${error}`);
			setErrors({ form: error instanceof Error ? error.message : 'Failed to save default worker pools' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
				<DialogTitle>Default Worker Pool</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						{errors.form && (
							<Typography color="error" variant="body2">{errors.form}</Typography>
						)}
						{loading ? (
							<Box display="flex" justifyContent="center" py={3}>
								<CircularProgress color="secondary" size={32} />
							</Box>
						) : (
							<>
								<FormControl
									fullWidth
									size="small"
									error={Boolean(errors.gsmWorkerPoolId)}
									disabled={gsmPools.length === 0}
								>
									<InputLabel id="default-gsm-worker-pool-label" shrink>
										GSM Worker Pool
									</InputLabel>
									<Select
										labelId="default-gsm-worker-pool-label"
										label="GSM Worker Pool"
										value={form.gsmWorkerPoolId}
										displayEmpty
										renderValue={(selected) => renderPoolValue(selected, gsmPools)}
										onChange={(e) =>
											setForm((prev) => ({ ...prev, gsmWorkerPoolId: e.target.value as string }))
										}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{gsmPools.map((pool) => (
											<MenuItem key={pool._id} value={pool._id}>
												{pool.poolName}
											</MenuItem>
										))}
									</Select>
									{errors.gsmWorkerPoolId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
											{errors.gsmWorkerPoolId}
										</Typography>
									)}
									{gsmPools.length === 0 && !errors.gsmWorkerPoolId && (
										<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
											No GSM worker pools found. Create one in Worker Pool administration.
										</Typography>
									)}
								</FormControl>

								<FormControl
									fullWidth
									size="small"
									error={Boolean(errors.whatsappWorkerPoolId)}
									disabled={whatsappPools.length === 0}
								>
									<InputLabel id="default-whatsapp-worker-pool-label" shrink>
										WhatsApp Worker Pool
									</InputLabel>
									<Select
										labelId="default-whatsapp-worker-pool-label"
										label="WhatsApp Worker Pool"
										value={form.whatsappWorkerPoolId}
										displayEmpty
										renderValue={(selected) => renderPoolValue(selected, whatsappPools)}
										onChange={(e) =>
											setForm((prev) => ({ ...prev, whatsappWorkerPoolId: e.target.value as string }))
										}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{whatsappPools.map((pool) => (
											<MenuItem key={pool._id} value={pool._id}>
												{pool.poolName}
											</MenuItem>
										))}
									</Select>
									{errors.whatsappWorkerPoolId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
											{errors.whatsappWorkerPoolId}
										</Typography>
									)}
									{whatsappPools.length === 0 && !errors.whatsappWorkerPoolId && (
										<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
											No WhatsApp worker pools found. Create one in Worker Pool administration.
										</Typography>
									)}
								</FormControl>
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						variant="outlined"
						color="secondary"
						onClick={onClose}
						disabled={saving}
						startIcon={<Cancel />}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
						variant="contained"
						color="success"
						disabled={saving || loading}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
					severity={snackbar.severity}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default SystemNotificationDefaultWorkerPoolsDialog;
